import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ListBulletIcon } from '@heroicons/react/24/outline';
import Modal from '../../../shared/components/Modal';

const BULLET = '• ';
const COMMENT_MAX_LENGTH = 1000;

// lastIndexOf treats a negative start as 0, so position 0 needs its own case.
const lineStartOf = (text, position) => (position === 0 ? 0 : text.lastIndexOf('\n', position - 1) + 1);

// The start and end offsets of every line the selection touches.
function selectedLineRange(text, start, end) {
  const from = lineStartOf(text, start);
  const nextBreak = text.indexOf('\n', end);
  return [from, nextBreak === -1 ? text.length : nextBreak];
}

// A teacher's comment for one student. It opens in its own box so the whole text is visible while
// typing, and bullet lines ("• ") are supported: the Bullet button toggles them on the selected
// lines, and Enter on a bullet line starts the next bullet (Enter on an empty one ends the list).
// `onDraftChange` gets the text as it's typed (so it can be backed up); `initialDraft` reopens the
// box on text restored from such a backup.
function CommentModal({ studentName, value, initialDraft, onDraftChange, onClose, onSave }) {
  const [draft, setDraft] = useState(initialDraft ?? value);
  const textareaRef = useRef(null);
  const pendingSelection = useRef(null);

  useEffect(() => {
    onDraftChange?.(draft);
  }, [draft, onDraftChange]);

  // Applies a selection change after React has put the new text in the textarea.
  useLayoutEffect(() => {
    if (pendingSelection.current && textareaRef.current) {
      const [start, end] = pendingSelection.current;
      textareaRef.current.setSelectionRange(start, end);
      pendingSelection.current = null;
    }
  }, [draft]);

  const replace = (nextText, selectionStart, selectionEnd = selectionStart) => {
    pendingSelection.current = [selectionStart, selectionEnd];
    setDraft(nextText.slice(0, COMMENT_MAX_LENGTH));
  };

  const handleBullet = () => {
    const el = textareaRef.current;
    const [from, to] = selectedLineRange(draft, el.selectionStart, el.selectionEnd);
    const lines = draft.slice(from, to).split('\n');
    const allBulleted = lines.every((line) => line.startsWith(BULLET));
    const next = lines.map((line) => (allBulleted ? line.slice(BULLET.length) : line.startsWith(BULLET) ? line : BULLET + line));
    const block = next.join('\n');
    replace(draft.slice(0, from) + block + draft.slice(to), from, from + block.length);
    el.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;

    const el = e.currentTarget;
    const { selectionStart, selectionEnd } = el;
    const lineStart = lineStartOf(draft, selectionStart);
    const line = draft.slice(lineStart, selectionStart);
    if (!line.startsWith(BULLET)) return;

    e.preventDefault();
    if (line === BULLET) {
      // Enter on an empty bullet ends the list.
      replace(draft.slice(0, lineStart) + draft.slice(selectionEnd), lineStart);
      return;
    }
    const insert = `\n${BULLET}`;
    replace(draft.slice(0, selectionStart) + insert + draft.slice(selectionEnd), selectionStart + insert.length);
  };

  const handleSave = () => {
    // Drop bullets that were left empty, then trim the ends.
    const cleaned = draft
      .split('\n')
      .filter((line) => line.trim() !== BULLET.trim())
      .join('\n')
      .trim();
    onSave(cleaned);
  };

  return (
    <Modal title={`Comment — ${studentName}`} onClose={onClose} maxWidthClass="max-w-lg">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleBullet}
            className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            <ListBulletIcon className="h-4 w-4" />
            Bullets
          </button>
          <span className="text-xs text-gray-500">
            {draft.length} / {COMMENT_MAX_LENGTH}
          </span>
        </div>

        <textarea
          ref={textareaRef}
          autoFocus
          rows={9}
          maxLength={COMMENT_MAX_LENGTH}
          placeholder="Write a comment for the student. Use Bullets for a list."
          aria-label={`Comment for ${studentName}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
        />
      </div>

      <div className="flex justify-between gap-2 pt-2">
        <button
          type="button"
          onClick={() => onSave('')}
          disabled={!value && !draft}
          className="rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:hover:bg-transparent"
        >
          Clear
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-md bg-[#0b4a71] px-4 py-2 text-sm font-medium text-white hover:bg-[#3f6d8e]"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default CommentModal;
